import { type ReactElement } from 'react'
import { TListItem } from 'app/components/terros'
import { PrefetchedTeamInfo } from 'app/components/entityInfo/TeamInfo'
import { TButton } from 'app/components/buttons'
import { isNotEmpty, type CompanyData, type TeamId, type TinyTeam } from '@terros/common'
import { Group, Text } from '@mantine/core'
import { useToastError } from '@hooks'
import { getLevelName, SizePrimitives, useRoles, useTeam, type MaterialSymbolType, type Team } from '@front/model'
import { MaterialSymbolIcon } from '../../terros/icon/MaterialSymbolIcon'
import { ManagersDisplay } from './ManagersDisplay'

export type TeamItemProps = {
  company: CompanyData
  parentOpen: boolean
  team: Team
  showManagers?: boolean
  onSelect?: (team: TinyTeam) => void
  selectedTeams?: TeamId[]
  expandedTeamIds?: TeamId[]
  activeTeamId?: TeamId
  hideArrow?: boolean
}

export const TeamItemWithoutChildren = ({
  parentOpen,
  team,
  company,
  showManagers,
  onSelect,
  selectedTeams,
  expandedTeamIds,
  hideArrow,
  activeTeamId,
  open,
  toggle,
}: TeamItemProps & { open: boolean; toggle: VoidFunction }): ReactElement => {
  const { data, error, isLoading } = useTeam(team.teamId, parentOpen)
  useToastError(error)
  const { data: roles, error: roleError } = useRoles()
  useToastError(roleError)

  const teamType = getLevelName(company, team.level)

  const collapsible = isNotEmpty(team.children)
  const forceExpanded = collapsible && Boolean(expandedTeamIds?.includes(team.teamId))
  const icon = getIcon(open, collapsible)
  const isSelected = selectedTeams?.includes(team.teamId)
  const isActive = activeTeamId === team.teamId

  return (
    <TListItem
      buttonProps={{ variant: isSelected || isActive ? 'light' : 'subtle' }}
      rightSection={
        <>
          <ManagersDisplay loading={isLoading} members={data?.members} roles={roles} visible={showManagers} />
          {isSelected && <TButton buttonType='icon-full' icon='check' />}
          {!hideArrow && <TButton buttonType='icon-full' icon='chevron_forward' variant='gray' />}
        </>
      }
      onClick={() => onSelect?.(team.toData())}
    >
      {team.toData().isDeleted && <MaterialSymbolIcon icon='delete' filled size={20} className='mr-4 opacity-50' />}
      <Group gap={SizePrimitives['3']} miw={0} wrap='nowrap'>
        <TButton
          buttonType='icon-full'
          disabled={!collapsible || forceExpanded}
          icon={icon}
          tooltip={forceExpanded ? 'Expanded to show the selected team' : undefined}
          variant='ghost'
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            toggle()
          }}
        />
        {team && <PrefetchedTeamInfo avatarOnly linked={false} size='md' team={team.toData()} />}
        <Text truncate>
          {teamType}: {team.name}
        </Text>
      </Group>
    </TListItem>
  )
}

function getIcon(open: boolean, collapsible: boolean): MaterialSymbolType {
  if (!collapsible) return 'remove'
  return open ? 'expand_more' : 'chevron_right'
}
